import { Fragment, useState, useEffect } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  ShoppingCartIcon,
  XMarkIcon,
  UserCircleIcon,
  GlobeAltIcon,
  ClockIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectItems } from '../cart/cartSlice';
import { selectUserInfo } from '../user/userSlice';
import { selectLoggedInUser } from '../auth/authSlice';
import { useLanguage } from '../../context/LanguageContext';
import WhatsAppFloat from '../common/WhatsAppFloat';


const navigation = [
  { name: 'home', link: '/home', user: true, admin: true },
  { name: 'about', link: '/about', user: true, admin: true },
  { name: 'products', link: '/products', user: true, admin: true },
  { name: 'faq', link: '/faq', user: true, admin: true },
  { name: 'contact', link: '/contact', user: true, admin: true },
  { name: 'payments', link: '/payments', user: true, admin: true },
  { name: 'refund', link: '/refund-policy', user: true, admin: true },
];

const adminNavigation = [
  { name: 'products', link: '/admin', admin: true },
  { name: 'orders', link: '/admin/orders', admin: true },
];

const userNavigation = [
  { name: 'myProfile', link: '/profile' },
  { name: 'myOrders', link: '/my-orders' },
  { name: 'signOut', link: '/logout' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function NavBar({ children }) {
  const items = useSelector(selectItems);
  const userInfo = useSelector(selectUserInfo);
  const user = useSelector(selectLoggedInUser);
  const { t, language, changeLanguage } = useLanguage();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Determine the user role for navigation filtering
  // const userRole = userInfo?.role || 'user';

  return (
    <>
      <div className="min-h-full">
        {/* Top Bar with Time and Contact */}
        <div className="bg-blue-600 text-white py-4 px-4 text-sm hidden md:block">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5">
                  <ClockIcon className="w-4 h-4" />
                  {formatTime(currentTime)} PKT
                </span>
                <span className="text-blue-200">|</span>
                <span>Mon - Sat: 10:00 AM - 11:00 PM</span>
              </div>
              <div className="flex items-center gap-4">
                <a href="https://wa.me/923272341839" className="flex items-center gap-1.5 hover:text-blue-200 transition-colors">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  +92 327 2341839
                </a>
                <span className="text-blue-200">|</span>
                <a href="https://t.me/923272341839" className="flex items-center gap-1.5 hover:text-blue-200 transition-colors">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                  Telegram
                </a>
              </div>
            </div>
          </div>

          <Disclosure
            as="nav"
            className="bg-white sticky top-0 z-40 shadow-md border-b border-gray-200"
          >
            {({ open }) => (
              <>
                <div className="w-full px-4 sm:px-6 lg:px-8">
                  <div className="flex h-16 items-center justify-between">
                    {/* Logo and Navigation */}
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <Link to="/" className="block">
                          <img
                            className="h-9 w-9 rounded-lg transition-all duration-300 hover:opacity-80"
                            src="/ecommerce.png"
                            alt="SubifyHub"
                          />
                        </Link>
                      </div>
                      <Link
                        to="/"
                        className="text-slate-900 font-bold text-xl tracking-wide hidden sm:block transition-colors duration-200 hover:text-blue-600"
                      >
                        SubifyHub
                      </Link>

                      {/* Desktop Navigation */}
                      <div className="hidden md:block">
                        <div className="ml-8 flex items-center gap-1">
                          {navigation.map((item) => (
                              <Link
                                key={item.name}
                                to={item.link}
                                className={classNames(
                                  'px-4 py-2 text-sm font-normal tracking-wide transition-all duration-200 rounded',
                                  item.current
                                    ? 'text-blue-600 bg-blue-50'
                                    : 'text-slate-700 hover:text-blue-600 hover:bg-blue-50/50'
                                )}
                                aria-current={item.current ? 'page' : undefined}
                              >
                                {t(item.name)}
                              </Link>
                          ))}
                          {/* Admin Navigation */}
                          {userInfo?.role === 'admin' && adminNavigation.map((item) => (
                              <Link
                                key={item.name + '-admin'}
                                to={item.link}
                                className={classNames(
                                  'px-4 py-2 text-sm font-normal tracking-wide transition-all duration-200 rounded',
                                  'text-slate-700 hover:text-blue-600 hover:bg-blue-50/50'
                                )}
                              >
                                {t(item.name)}
                              </Link>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Desktop Right Section */}
                    <div className="hidden md:block">
                      <div className="flex items-center gap-3">
                        {/* Telegram Button */}
                        <a
                          href="https://t.me/923272341839"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-lg p-2.5 transition-all duration-200 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                          title="Contact us on Telegram"
                        >
                          <svg className="h-5 w-5" fill="#0088cc" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                          </svg>
                        </a>

                        {/* WhatsApp Button */}
                        <a
                          href="https://wa.me/923272341839"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-lg p-2.5 transition-all duration-200 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500/50"
                          title="Contact us on WhatsApp"
                        >
                          <svg className="h-5 w-5" fill="#25D366" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 9.798h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741 .981.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                          </svg>
                        </a>

                        {/* Language Dropdown */}
                        <Menu as="div" className="relative">
                          <Menu.Button className="flex items-center gap-1.5 rounded-lg bg-blue-50 px-3.5 py-2.5 text-slate-700 transition-all duration-200 hover:bg-blue-100 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50">
                            <GlobeAltIcon className="h-5 w-5" aria-hidden="true" />
                            <span className="text-sm font-medium">{language.toUpperCase()}</span>
                          </Menu.Button>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-150"
                            enterFrom="transform opacity-0 scale-95 -translate-y-1"
                            enterTo="transform opacity-100 scale-100 translate-y-0"
                            leave="transition ease-in duration-100"
                            leaveFrom="transform opacity-100 scale-100 translate-y-0"
                            leaveTo="transform opacity-0 scale-95 -translate-y-1"
                          >
                            <Menu.Items className="absolute right-0 z-10 mt-3 w-48 origin-top-right rounded-xl bg-white py-2 shadow-elegant ring-1 ring-blue-200 focus:outline-none">
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => changeLanguage('en')}
                                    className={classNames(
                                      'block w-full px-4 py-2.5 text-sm text-left transition-colors duration-150',
                                      active ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50',
                                      language === 'en' && 'bg-blue-100 text-blue-700 font-medium'
                                    )}
                                  >
                                    🇬🇧 English
                                  </button>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => changeLanguage('fr')}
                                    className={classNames(
                                      'block w-full px-4 py-2.5 text-sm text-left transition-colors duration-150',
                                      active ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50',
                                      language === 'fr' && 'bg-blue-100 text-blue-700 font-medium'
                                    )}
                                  >
                                    🇫🇷 Français
                                  </button>
                                )}
                              </Menu.Item>
                            </Menu.Items>
                          </Transition>
                        </Menu>

                        {/* Cart Button */}
                        <Link to={user ? "/cart" : "/login"} className="relative group">
                          <button
                            type="button"
                            className="rounded-lg bg-blue-50 p-2.5 text-slate-700 transition-all duration-200 hover:bg-blue-100 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                          >
                            <span className="sr-only">{t('viewCart')}</span>
                            <ShoppingCartIcon className="h-5 w-5" aria-hidden="true" />
                          </button>
                          {user && items.length > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold">
                              {items.length}
                            </span>
                          )}
                        </Link>

                        {/* Profile Dropdown or Login Button */}
                        {user ? (
                        <Menu as="div" className="relative">
                          <Menu.Button className="flex items-center rounded-full p-0.5 transition-all duration-200 hover:ring-2 hover:ring-blue-500/30 focus:outline-none focus:ring-2 focus:ring-blue-500/50">
                            <span className="sr-only">Open user menu</span>
                            <UserCircleIcon className="h-9 w-9 text-slate-700 hover:text-blue-600 transition-colors duration-200" />
                          </Menu.Button>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-150"
                            enterFrom="transform opacity-0 scale-95 -translate-y-1"
                            enterTo="transform opacity-100 scale-100 translate-y-0"
                            leave="transition ease-in duration-100"
                            leaveFrom="transform opacity-100 scale-100 translate-y-0"
                            leaveTo="transform opacity-0 scale-95 -translate-y-1"
                          >
                            <Menu.Items className="absolute right-0 z-10 mt-3 w-52 origin-top-right rounded-xl bg-white py-2 shadow-elegant ring-1 ring-slate-200 focus:outline-none">
                              <div className="px-4 py-3 border-b border-slate-100">
                                <p className="text-sm font-medium text-slate-900 truncate">
                                  {userInfo?.name || 'User'}
                                </p>
                                <p className="text-xs text-slate-500 truncate mt-0.5">
                                  {userInfo?.email || ''}
                                </p>
                              </div>
                              <div className="py-1">
                                {userNavigation.map((item) => (
                                  <Menu.Item key={item.name}>
                                    {({ active }) => (
                                      <Link
                                        to={item.link}
                                        className={classNames(
                                          'block px-4 py-2.5 text-sm transition-colors duration-150',
                                          active
                                            ? 'bg-sky-50 text-sky-700'
                                            : 'text-slate-700 hover:bg-slate-50'
                                        )}
                                      >
                                        {t(item.name)}
                                      </Link>
                                    )}
                                  </Menu.Item>
                                ))}
                              </div>
                            </Menu.Items>
                          </Transition>
                        </Menu>
                        ) : (
                          <Link
                            to="/login"
                            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                          >
                            <ArrowRightOnRectangleIcon className="h-5 w-5" />
                            {t('login')}
                          </Link>
                        )}
                      </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="flex md:hidden items-center gap-3">
                      {/* Telegram Button Mobile */}
                      <a
                        href="https://t.me/+923272341839"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-lg p-2 transition-all duration-200 hover:bg-blue-50"
                        title="Contact us on Telegram"
                      >
                        <svg className="h-5 w-5" fill="#0088cc" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                        </svg>
                      </a>

                      {/* WhatsApp Button Mobile */}
                      <a
                        href="https://wa.me/923272341839"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-lg p-2 transition-all duration-200 hover:bg-green-50"
                        title="Contact us on WhatsApp"
                      >
                        <svg className="h-5 w-5" fill="#25D366" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 9.798h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741 .981.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                        </svg>
                      </a>

                      <Link to={user ? "/cart" : "/login"} className="relative">
                        <button
                          type="button"
                          className="rounded-lg bg-blue-50 p-2 text-slate-700 transition-all duration-200 hover:bg-blue-100 hover:text-blue-600"
                        >
                          <ShoppingCartIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                        {user && items.length > 0 && (
                          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold">
                            {items.length}
                          </span>
                        )}
                      </Link>
                      <Disclosure.Button className="inline-flex items-center justify-center rounded-lg bg-blue-50 p-2 text-slate-700 transition-all duration-200 hover:bg-blue-100 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50">
                        <span className="sr-only">Open main menu</span>
                        {open ? (
                          <XMarkIcon className="block h-5 w-5" aria-hidden="true" />
                        ) : (
                          <Bars3Icon className="block h-5 w-5" aria-hidden="true" />
                        )}
                      </Disclosure.Button>
                    </div>
                  </div>
                </div>

                {/* Mobile Menu Panel */}
                <Transition
                  as={Fragment}
                  enter="transition duration-200 ease-out"
                  enterFrom="opacity-0 -translate-y-2"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition duration-150 ease-in"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 -translate-y-2"
                >
                  <Disclosure.Panel className="md:hidden border-t border-blue-200">
                    {/* Language Selector for Mobile */}
                    <div className="px-3 py-3 border-b border-blue-200">
                      <p className="text-xs font-medium text-slate-600 px-1 mb-2">Language</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => changeLanguage('en')}
                          className={classNames(
                            'flex-1 py-2 px-3 text-sm rounded-lg font-medium transition-all duration-200',
                            language === 'en'
                              ? 'bg-blue-600 text-white'
                              : 'bg-blue-50 text-slate-700 hover:text-blue-600'
                          )}
                        >
                          🇬🇧 English
                        </button>
                        <button
                          onClick={() => changeLanguage('fr')}
                          className={classNames(
                            'flex-1 py-2 px-3 text-sm rounded-lg font-medium transition-all duration-200',
                            language === 'fr'
                              ? 'bg-blue-600 text-white'
                              : 'bg-blue-50 text-slate-700 hover:text-blue-600'
                          )}
                        >
                          🇫🇷 Français
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1 px-3 py-4">
                      {navigation.map((item) => (
                          <Disclosure.Button
                            key={item.name}
                            as={Link}
                            to={item.link}
                            className={classNames(
                              'block rounded-lg px-4 py-3 text-sm font-normal transition-all duration-200',
                              item.current
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-slate-700 hover:bg-blue-50 hover:text-blue-600'
                            )}
                            aria-current={item.current ? 'page' : undefined}
                          >
                            {t(item.name)}
                          </Disclosure.Button>
                      ))}
                      {/* Admin Navigation Links */}
                      {user && userInfo?.role === 'admin' && adminNavigation.map((item) => (
                          <Disclosure.Button
                            key={item.name}
                            as={Link}
                            to={item.link}
                            className={classNames(
                              'block rounded-lg px-4 py-3 text-sm font-normal transition-all duration-200',
                              item.current
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-slate-700 hover:bg-blue-50 hover:text-blue-600'
                            )}
                            aria-current={item.current ? 'page' : undefined}
                          >
                            {t(item.name)}
                          </Disclosure.Button>
                      ))}
                    </div>

                    {user ? (
                    <div className="border-t border-blue-200 px-3 py-4">
                      <div className="flex items-center px-1 mb-4">
                        <UserCircleIcon className="h-10 w-10 text-slate-700" />
                        <div className="ml-3 min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {userInfo?.name || 'User'}
                          </p>
                          <p className="text-xs text-slate-600 truncate">
                            {userInfo?.email || ''}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {userNavigation.map((item) => (
                          <Disclosure.Button
                            key={item.name}
                            as={Link}
                            to={item.link}
                            className="block rounded-lg px-4 py-3 text-sm font-normal text-slate-700 transition-all duration-200 hover:bg-blue-50 hover:text-blue-600"
                          >
                            {t(item.name)}
                          </Disclosure.Button>
                        ))}
                      </div>
                    </div>
                    ) : (
                    <div className="border-t border-blue-200 px-3 py-4">
                      <Disclosure.Button
                        as={Link}
                        to="/login"
                        className="flex items-center justify-center gap-2 w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-blue-700"
                      >
                        <ArrowRightOnRectangleIcon className="h-5 w-5" />
                        {t('login')}
                      </Disclosure.Button>
                    </div>
                    )}
                  </Disclosure.Panel>
                </Transition>
              </>
            )}
          </Disclosure>

          {/* Main Content Area */}
          <main className="bg-gray-50 min-h-screen">
            <div className="w-full">
              {children}
            </div>
          </main>
          
          {/* Floating WhatsApp Button */}
          <WhatsAppFloat />
        </div>
    </>
  );
}

export default NavBar;
